import { useAuthActions, useAuthContext, useAxios } from '@healthblocks-io/core'
import { useSetRecoilState } from 'recoil'

import { useAuthenticedSession } from '@/modules/auth/hooks/useAuthenticatedSession'
import { useSessionCredentials } from '@/modules/auth/hooks/useSessionCredentials'
import { elapsedTimeAtom } from '@/modules/idle-timer/state/idle-timer.atoms'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'

function useLoginAndRegister() {
  const { signInWithEmailAndPassword, createUserWithEmailAndPassword } = useAuthActions()

  const axios = useAxios()
  const auth = useAuthContext()
  const setLastInteraction = useSetRecoilState(elapsedTimeAtom)
  const { setLockout } = useLockoutTime()
  const { updateLastLoggedIn } = useAuthenticedSession()
  const { saveCredentials } = useSessionCredentials()

  const handleLoginSuccess = async (tokens?: { accessToken?: string; refreshToken?: string }) => {
    setLockout(false)

    // Reset idle timer on successful login to avoid instantly locking again
    // due to a persisted "last interaction" timestamp from a previous session.
    setLastInteraction(Date.now())
    updateLastLoggedIn()

    const accessToken = tokens?.accessToken ?? auth.data.current?.access_token ?? ''
    const refreshToken = tokens?.refreshToken ?? auth.data.current?.refresh_token ?? ''

    if (accessToken && refreshToken) {
      await saveCredentials('user', accessToken, refreshToken)
    }
  }

  const signInWithAsMandate = async ({ email, password }: { email: string; password: string }) => {
    try {
      const response = await axios.post(`/auth/login?loginAsMandate=true`, {
        email,
        password,
      })

      const access_token = response.headers.authorization?.replace('Bearer ', '')

      const refresh_token = response.headers['refresh-token'] || response.headers['Refresh-Token']

      auth.setData(
        {
          anonymous: '',
          access_token,
          refresh_token,
        },
        true,
      )

      // Even for mandate login we need to clear the lock screen (otherwise the app
      // would immediately redirect back to /login again).
      await handleLoginSuccess({
        accessToken: access_token,
        refreshToken: refresh_token,
      })

      return response.data
    } catch (error) {
      console.error('Failed to login with mandate:', error)
      throw error
    }
  }

  return {
    login: async (...arguments_: Parameters<typeof signInWithEmailAndPassword>) => {
      const result = await signInWithEmailAndPassword(...arguments_)
      await handleLoginSuccess()
      return result
    },
    loginWithAsMandate: signInWithAsMandate,
    register: createUserWithEmailAndPassword,
  }
}

export { useLoginAndRegister }
